import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { 
  FiSearch, 
  FiUser, 
  FiDollarSign, 
  FiCheckCircle, 
  FiAlertCircle,
  FiCalendar,
  FiFileText,
  FiPrinter
} from 'react-icons/fi';
import './StatusPembayaran.css';

const StatusPembayaran = () => {
  const [searchType, setSearchType] = useState('nim'); // nim or name
  const [searchValue, setSearchValue] = useState('');
  const [loading, setLoading] = useState(false);
  const [studentData, setStudentData] = useState(null);
  const [pembayaranData, setPembayaranData] = useState([]);
  const [tagihanData, setTagihanData] = useState([]);
  const [showResult, setShowResult] = useState(false);

  const handleSearch = async (e) => {
    e.preventDefault();
    
    if (!searchValue.trim()) {
      toast.error('Mohon masukkan ' + (searchType === 'nim' ? 'NIM' : 'Nama'));
      return;
    }

    setLoading(true);
    setShowResult(false);

    try {
      // Search student
      const studentRes = await axios.get('/api/students', {
        params: searchType === 'nim' 
          ? { nim: searchValue } 
          : { search: searchValue, limit: 1 }
      });

      if (!studentRes.data.data || studentRes.data.data.length === 0) {
        toast.error('Mahasiswa tidak ditemukan');
        setStudentData(null);
        setPembayaranData([]);
        setTagihanData([]);
        setLoading(false);
        return;
      }

      const student = studentRes.data.data[0];
      setStudentData(student);

      // Fetch pembayaran dan tagihan
      const [pembayaranRes, tagihanRes] = await Promise.all([
        axios.get('/api/finance/pembayaran-mahasiswa', { 
          params: { nim: student.nim } 
        }),
        axios.get('/api/tagihan', { 
          params: { nim: student.nim } 
        })
      ]);

      setPembayaranData(pembayaranRes.data.data || []);
      setTagihanData(tagihanRes.data.data || []);
      setShowResult(true);
      toast.success('Data ditemukan');
    } catch (error) {
      toast.error('Gagal mengambil data');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setSearchValue('');
    setStudentData(null);
    setPembayaranData([]);
    setTagihanData([]);
    setShowResult(false);
  };

  const handlePrint = () => {
    window.print();
  };

  const formatRupiah = (angka) => {
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0
    }).format(angka);
  };

  const formatTanggal = (tanggal) => {
    return new Date(tanggal).toLocaleDateString('id-ID', {
      day: '2-digit',
      month: 'long',
      year: 'numeric'
    });
  };

  // Calculate statistics
  const totalPembayaran = pembayaranData.reduce((sum, p) => sum + (p.nominal_dibayar || 0), 0);
  const totalTagihan = tagihanData.reduce((sum, t) => sum + (t.jumlah || 0), 0);
  const tagihanLunas = tagihanData.filter(t => t.status === 'lunas');
  const tagihanBelumLunas = tagihanData.filter(t => t.status === 'belum_lunas');
  const totalTunggakan = tagihanBelumLunas.reduce((sum, t) => sum + (t.jumlah || 0), 0);
  const tagihanTerlambat = tagihanBelumLunas.filter(t => 
    new Date(t.deadline) < new Date()
  );

  return (
    <div className="status-pembayaran-page">
      {/* Header */}
      <div className="modern-header">
        <div className="header-background">
          <div className="header-pattern"></div>
        </div>
        <div className="header-content">
          <div className="header-text">
            <div className="header-icon-wrapper">
              <FiSearch className="header-icon" />
            </div>
            <div>
              <h1>Cek Status Pembayaran</h1>
              <p>Cari dan lihat riwayat pembayaran mahasiswa</p>
            </div>
          </div>
        </div>
      </div>

      {/* Search Form */}
      <div className="card search-card">
        <form onSubmit={handleSearch}>
          <div className="search-form">
            <div className="search-type-selector">
              <button
                type="button"
                className={`type-btn ${searchType === 'nim' ? 'active' : ''}`}
                onClick={() => setSearchType('nim')}
              >
                <FiUser /> Cari dengan NIM
              </button>
              <button
                type="button"
                className={`type-btn ${searchType === 'name' ? 'active' : ''}`}
                onClick={() => setSearchType('name')}
              >
                <FiUser /> Cari dengan Nama
              </button>
            </div>
            
            <div className="search-input-group">
              <div className="search-input-wrapper">
                <FiSearch className="search-icon" />
                <input
                  type="text"
                  className="search-input"
                  placeholder={searchType === 'nim' ? 'Masukkan NIM Mahasiswa...' : 'Masukkan Nama Mahasiswa...'}
                  value={searchValue}
                  onChange={(e) => setSearchValue(e.target.value)}
                  disabled={loading}
                />
              </div>
              <div className="search-buttons">
                <button 
                  type="submit" 
                  className="btn btn-primary"
                  disabled={loading}
                >
                  {loading ? 'Mencari...' : 'Cari'}
                </button>
                {showResult && (
                  <button 
                    type="button" 
                    className="btn btn-secondary"
                    onClick={handleReset}
                  >
                    Reset
                  </button>
                )}
              </div>
            </div>
          </div>
        </form>
      </div>

      {/* Results */}
      {showResult && studentData && (
        <>
          {/* Student Info */}
          <div className="card student-card">
            <div className="card-header">
              <h3><FiUser /> Informasi Mahasiswa</h3>
              <button className="btn-icon" onClick={handlePrint}>
                <FiPrinter /> Print
              </button>
            </div>
            <div className="student-info-grid">
              <div className="info-item">
                <div className="info-label">NIM</div>
                <div className="info-value">{studentData.nim}</div>
              </div>
              <div className="info-item">
                <div className="info-label">Nama Lengkap</div>
                <div className="info-value">{studentData.name}</div>
              </div>
              <div className="info-item">
                <div className="info-label">Program Studi</div>
                <div className="info-value">{studentData.program || '-'}</div>
              </div>
              <div className="info-item">
                <div className="info-label">Angkatan</div>
                <div className="info-value">{studentData.angkatan || '-'}</div>
              </div>
              <div className="info-item">
                <div className="info-label">Email</div>
                <div className="info-value">{studentData.email || '-'}</div>
              </div>
              <div className="info-item">
                <div className="info-label">Status</div>
                <div className="info-value">
                  <span className={`badge ${studentData.status === 'active' ? 'badge-success' : 'badge-danger'}`}>
                    {studentData.status === 'active' ? 'Aktif' : 'Tidak Aktif'}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Statistics */}
          <div className="stats-grid">
            <div className="stat-card stat-primary">
              <div className="stat-icon">
                <FiDollarSign />
              </div>
              <div className="stat-content">
                <div className="stat-label">Total Pembayaran</div>
                <div className="stat-value">{formatRupiah(totalPembayaran)}</div>
                <div className="stat-desc">{pembayaranData.length} transaksi</div>
              </div>
            </div>

            <div className="stat-card stat-warning">
              <div className="stat-icon">
                <FiFileText />
              </div>
              <div className="stat-content">
                <div className="stat-label">Total Tagihan</div>
                <div className="stat-value">{formatRupiah(totalTagihan)}</div>
                <div className="stat-desc">{tagihanData.length} tagihan</div>
              </div>
            </div>

            <div className="stat-card stat-danger">
              <div className="stat-icon">
                <FiAlertCircle />
              </div>
              <div className="stat-content">
                <div className="stat-label">Total Tunggakan</div>
                <div className="stat-value">{formatRupiah(totalTunggakan)}</div>
                <div className="stat-desc">{tagihanBelumLunas.length} belum lunas</div>
              </div>
            </div>

            <div className="stat-card stat-success">
              <div className="stat-icon">
                <FiCheckCircle />
              </div>
              <div className="stat-content">
                <div className="stat-label">Tagihan Lunas</div>
                <div className="stat-value">{tagihanLunas.length}</div>
                <div className="stat-desc">dari {tagihanData.length} tagihan</div>
              </div>
            </div>
          </div>

          {/* Tagihan Terlambat Alert */}
          {tagihanTerlambat.length > 0 && (
            <div className="alert alert-danger">
              <FiAlertCircle className="alert-icon" />
              <div className="alert-content">
                <h4>Perhatian!</h4>
                <p>Terdapat {tagihanTerlambat.length} tagihan yang sudah melewati deadline pembayaran.</p>
              </div>
            </div>
          )}

          {/* Tagihan Section */}
          <div className="card">
            <div className="card-header">
              <h3><FiFileText /> Daftar Tagihan</h3>
            </div>
            {tagihanData.length > 0 ? (
              <div className="table-responsive">
                <table className="modern-table">
                  <thead>
                    <tr>
                      <th>No</th>
                      <th>Jenis Tagihan</th>
                      <th>Semester</th>
                      <th>Tahun Ajaran</th>
                      <th>Jumlah</th>
                      <th>Deadline</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {tagihanData.map((tagihan, index) => {
                      const isOverdue = tagihan.status === 'belum_lunas' && 
                                       new Date(tagihan.deadline) < new Date();
                      return (
                        <tr key={tagihan._id} className={isOverdue ? 'row-danger' : ''}>
                          <td>{index + 1}</td>
                          <td><strong>{tagihan.jenis_tagihan}</strong></td>
                          <td>{tagihan.semester}</td>
                          <td>{tagihan.tahun_ajaran}</td>
                          <td><strong>{formatRupiah(tagihan.jumlah)}</strong></td>
                          <td>{formatTanggal(tagihan.deadline)}</td>
                          <td>
                            <span className={`badge ${
                              tagihan.status === 'lunas' ? 'badge-success' : 
                              isOverdue ? 'badge-danger' : 'badge-warning'
                            }`}>
                              {tagihan.status === 'lunas' ? 'Lunas' : 
                               isOverdue ? 'Terlambat' : 'Belum Lunas'}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="empty-state">
                <FiFileText className="empty-icon" />
                <p>Tidak ada tagihan untuk mahasiswa ini</p>
              </div>
            )}
          </div>

          {/* Pembayaran Section */}
          <div className="card">
            <div className="card-header">
              <h3><FiDollarSign /> Riwayat Pembayaran</h3>
            </div>
            {pembayaranData.length > 0 ? (
              <div className="table-responsive">
                <table className="modern-table">
                  <thead>
                    <tr>
                      <th>No</th>
                      <th>Tanggal</th>
                      <th>Jenis Biaya</th>
                      <th>Nominal Biaya</th>
                      <th>Nominal Dibayar</th>
                      <th>Metode</th>
                      <th>Keterangan</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pembayaranData.map((pembayaran, index) => (
                      <tr key={pembayaran._id}>
                        <td>{index + 1}</td>
                        <td>
                          <div className="date-cell">
                            <FiCalendar className="date-icon" />
                            {formatTanggal(pembayaran.tanggal_pembayaran)}
                          </div>
                        </td>
                        <td><strong>{pembayaran.jenis_biaya}</strong></td>
                        <td>{formatRupiah(pembayaran.nominal_biaya)}</td>
                        <td><strong className="text-success">{formatRupiah(pembayaran.nominal_dibayar)}</strong></td>
                        <td>
                          <span className="badge badge-info">
                            {pembayaran.metode_pembayaran}
                          </span>
                        </td>
                        <td>{pembayaran.keterangan || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="empty-state">
                <FiDollarSign className="empty-icon" />
                <p>Belum ada riwayat pembayaran untuk mahasiswa ini</p>
              </div>
            )}
          </div>
        </>
      )}

      {/* Empty State - No Search Yet */}
      {!showResult && !loading && (
        <div className="empty-state-large">
          <FiSearch className="empty-icon-large" />
          <h3>Cari Data Mahasiswa</h3>
          <p>Gunakan form di atas untuk mencari data pembayaran mahasiswa berdasarkan NIM atau Nama</p>
        </div>
      )}
    </div>
  );
};

export default StatusPembayaran;
